import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { DateInput } from "../../components/ui/DateInput";
import { toast } from "react-toastify";
import Sidebar from "../../components/Sidebar";
import { AppHeader } from "../../components/layout/AppHeader";
import { AppointmentAPI } from "../../services/appointment.service";
import type { AppointmentListItem, AppointmentSlot } from "../../services/appointment.service";
import { DoctorAPI } from "../../services/doctor.service";
import type { BlockCancelScope, DoctorListItem, DoctorBlockTime } from "../../services/doctor.service";
import { BookAppointmentModal } from "../../components/appointments/BookAppointmentModal";
import { BlockDoctorTimeModal } from "../../components/doctors/BlockDoctorTimeModal";
import { AppointmentStatusBadge } from "../../components/appointments/AppointmentStatusBadge";
import { useRole } from "../../hooks/useRole";
import { getCurrentUser } from "aws-amplify/auth";
import { QueueAPI } from "../../services/queue.service";
import type { QueueEntryListItem } from "../../services/queue.service";
import { QueueStateBadge } from "../../components/queue/QueueStateBadge";

// ── Constants ─────────────────────────────────────────────────────────────────
const HOUR_PX = 64;
const DAY_START = 7;
const DAY_END = 21;
const HOURS = Array.from({ length: DAY_END - DAY_START }, (_, i) => DAY_START + i);
const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_LABELS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

const DOCTOR_PALETTE: { hex: string; light: string }[] = [
  { hex: "#3b82f6", light: "#bfdbfe" },
  { hex: "#f97316", light: "#fed7aa" },
  { hex: "#8b5cf6", light: "#ddd6fe" },
  { hex: "#10b981", light: "#a7f3d0" },
  { hex: "#ef4444", light: "#fecaca" },
  { hex: "#06b6d4", light: "#a5f3fc" },
  { hex: "#f59e0b", light: "#fde68a" },
  { hex: "#ec4899", light: "#fbcfe8" },
];

// Dead-end statuses render grey instead of the doctor's color - the doctor
// palette is meant to distinguish WHOSE appointment this is at a glance,
// which stops mattering once it's cancelled/no-show; keeping it in full
// color made a dead appointment look identical to a live one until you
// spotted the small status badge.
const DEAD_APPT_STATUSES = new Set(["cancelled", "no_show"]);
const DEAD_APPT_COLOR = { hex: "#94a3b8", light: "#e5e9ef" };
const getApptColor = (status: string, colorIdx: number) =>
  DEAD_APPT_STATUSES.has(status) ? DEAD_APPT_COLOR : DOCTOR_PALETTE[colorIdx];

// ── Helpers ───────────────────────────────────────────────────────────────────
const toDateStr = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const fmtTime = (iso: string) =>
  new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

const fmtLongDate = (dateStr: string) =>
  new Date(dateStr + "T00:00:00").toLocaleDateString("en-IN", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

const getWeekDates = (dateStr: string): string[] => {
  const d = new Date(dateStr + "T00:00:00");
  const dow = d.getDay(); // 0=Sun
  const monday = new Date(d);
  monday.setDate(d.getDate() - (dow === 0 ? 6 : dow - 1));
  return Array.from({ length: 7 }, (_, i) => {
    const day = new Date(monday);
    day.setDate(monday.getDate() + i);
    return toDateStr(day);
  });
};

// yToHour/roundToQuarterHour/hourToLocalDT convert between the drag gesture's
// pixel coordinates and the same decimal-hour space getBlockStyle already
// uses (top = (hour - DAY_START) * HOUR_PX) — one piece of math, read in
// both directions, so drag-select and block rendering can't drift apart.
const yToHour = (relativeY: number) =>
  Math.min(Math.max(DAY_START + relativeY / HOUR_PX, DAY_START), DAY_END);

const roundToQuarterHour = (h: number) => Math.round(h * 4) / 4;

const hourToLocalDT = (dateStr: string, hour: number) => {
  const totalMin = Math.round(hour * 60);
  const hh = String(Math.floor(totalMin / 60)).padStart(2, "0");
  const mm = String(totalMin % 60).padStart(2, "0");
  return `${dateStr}T${hh}:${mm}`;
};

interface DragState {
  dateStr: string;
  containerTop: number;
  startY: number;
  currentY: number;
}

// ── Doctor blocks ─────────────────────────────────────────────────────────────
const BLOCK_STRIPES =
  "repeating-linear-gradient(45deg, rgba(239,68,68,0.08), rgba(239,68,68,0.08) 6px, rgba(239,68,68,0.16) 6px, rgba(239,68,68,0.16) 12px)";
const EDGE_MARKER_PX = 18;

const isSeriesBlock = (b: DoctorBlockTime) => Boolean(b.recurrence_rule || b.recurrence_parent_id);

// The last instant a block covers - end_time is exclusive, so an all-day
// block ending exactly at midnight doesn't spill onto the next day.
const blockLastInstant = (b: DoctorBlockTime) => new Date(new Date(b.end_time).getTime() - 1);

const fmtShortDay = (d: Date) =>
  d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });

const fmtBlockRange = (b: DoctorBlockTime) => {
  const start = new Date(b.start_time);
  if (b.all_day) {
    const last = blockLastInstant(b);
    return toDateStr(start) === toDateStr(last)
      ? `${fmtShortDay(start)} (all day)`
      : `${fmtShortDay(start)} – ${fmtShortDay(last)} (all day)`;
  }
  const end = new Date(b.end_time);
  return toDateStr(start) === toDateStr(blockLastInstant(b))
    ? `${fmtShortDay(start)}, ${fmtTime(b.start_time)} – ${fmtTime(b.end_time)}`
    : `${fmtShortDay(start)} ${fmtTime(b.start_time)} – ${fmtShortDay(end)} ${fmtTime(b.end_time)}`;
};

// Where the part of a block that falls on dateStr sits in that day's
// DAY_START-DAY_END grid. A segment entirely outside the grid's hours comes
// back as a thin edge marker (top or bottom) rather than a misplaced bar.
const getBlockSegment = (
  block: DoctorBlockTime, dateStr: string,
): { style: React.CSSProperties; edge: "before" | "after" | null } => {
  const dayStartMs = new Date(`${dateStr}T00:00:00`).getTime();
  const hourOf = (iso: string) => (new Date(iso).getTime() - dayStartMs) / 3_600_000;
  const startH = Math.max(hourOf(block.start_time), 0);
  const endH = Math.min(hourOf(block.end_time), 24);
  const visibleStart = Math.max(startH, DAY_START);
  const visibleEnd = Math.min(endH, DAY_END);
  if (visibleEnd <= visibleStart) {
    const edge = endH <= DAY_START ? "before" : "after";
    const top = edge === "before" ? 0 : (DAY_END - DAY_START) * HOUR_PX - EDGE_MARKER_PX;
    return { edge, style: { top: `${top}px`, height: `${EDGE_MARKER_PX}px` } };
  }
  return {
    edge: null,
    style: {
      top: `${(visibleStart - DAY_START) * HOUR_PX}px`,
      height: `${Math.max((visibleEnd - visibleStart) * HOUR_PX, 20)}px`,
    },
  };
};

// ── Status dot ────────────────────────────────────────────────────────────────
const STATUS_DOT: Record<string, string> = {
  scheduled:   "bg-blue-500",
  confirmed:   "bg-cyan-500",
  in_progress: "bg-amber-500",
  completed:   "bg-green-500",
  cancelled:   "bg-slate-400",
  no_show:     "bg-red-400",
};
const STATUS_LABEL: Record<string, string> = {
  scheduled:   "Scheduled",
  confirmed:   "Confirmed",
  in_progress: "In Progress",
  completed:   "Completed",
  cancelled:   "Cancelled",
  no_show:     "No Show",
};
const StatusDot: React.FC<{ status: string }> = ({ status }) => (
  <span className={`inline-flex items-center gap-1 text-[9px] font-medium text-white rounded-full px-1.5 py-0.5 flex-shrink-0 ${STATUS_DOT[status] ?? "bg-slate-400"}`}>
    {STATUS_LABEL[status] ?? status}
  </span>
);

// ── Types ─────────────────────────────────────────────────────────────────────
type ViewMode = "day" | "week" | "month";

type EnrichedAppt = AppointmentListItem & {
  start_time: string;
  end_time: string;
  doctor_name?: string;
  colorIdx: number;
};

// ── Page ──────────────────────────────────────────────────────────────────────
const AppointmentCalendarPage: React.FC = () => {
  const routerNavigate = useNavigate();
  // Reachable with appointments.view alone; booking is appointments.create.
  const canBookAppointment = useRole().canOr("appointments.create", true);
  const openAppointment = useCallback(
    (appointmentId: string) => routerNavigate(`/appointments/confirmation/${appointmentId}`),
    [routerNavigate],
  );
  const [viewMode, setViewMode] = useState<ViewMode>("day");
  const [selectedDate, setSelectedDate] = useState(toDateStr(new Date()));
  const [appointments, setAppointments] = useState<AppointmentListItem[]>([]);
  const [slots, setSlots] = useState<AppointmentSlot[]>([]);
  const [doctors, setDoctors] = useState<DoctorListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [isBookOpen, setIsBookOpen] = useState(false);
  const [hoveredAppt, setHoveredAppt] = useState<EnrichedAppt | null>(null);
  const [showRightPanel, setShowRightPanel] = useState(false);
  const [filterDoctorId, setFilterDoctorId] = useState<string | null>(null);
  const [blocks, setBlocks] = useState<DoctorBlockTime[]>([]);
  const [blockModalDoctorId, setBlockModalDoctorId] = useState<string | null>(null);
  const [blockModalRange, setBlockModalRange] = useState<{ start: string; end: string } | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);
  const [cancelTarget, setCancelTarget] = useState<DoctorBlockTime | null>(null);
  const [cancellingBlock, setCancellingBlock] = useState(false);
  const { isAdmin, isDoctor, canOr } = useRole();
  const canBlockAnyDoctor = canOr("doctors.manage", isAdmin);
  const canBlockOwnTime = canOr("doctors.manage_schedule", isDoctor);
  const [currentUserId, setCurrentUserId] = useState("");

  useEffect(() => {
    getCurrentUser().then((u) => setCurrentUserId(u.userId)).catch(() => {});
  }, []);
  const [walkinsByDoctor, setWalkinsByDoctor] = useState<Record<string, QueueEntryListItem[]>>({});

  // 6-month navigation range
  const today = useMemo(() => toDateStr(new Date()), []);
  const minDate = useMemo(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 6); return toDateStr(d);
  }, []);
  const maxDate = useMemo(() => {
    const d = new Date(); d.setMonth(d.getMonth() + 6); return toDateStr(d);
  }, []);

  const weekDates = useMemo(() => getWeekDates(selectedDate), [selectedDate]);

  useEffect(() => {
    const loadDoctors = () => DoctorAPI.list({ is_active: true }).then(setDoctors).catch(() => {});
    loadDoctors();
    window.addEventListener("doctorUpdated", loadDoctors);
    return () => window.removeEventListener("doctorUpdated", loadDoctors);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let appts: AppointmentListItem[];
      let slotList: AppointmentSlot[];

      // Use plain date strings ("YYYY-MM-DD") which JS parses as UTC midnight —
      // avoids the local-midnight-to-UTC shift that loses a day in IST (+05:30).
      if (viewMode === "day") {
        const isoDate = new Date(selectedDate).toISOString();
        [appts, slotList] = await Promise.all([
          AppointmentAPI.list({ date: isoDate }),
          AppointmentAPI.listSlots({ date: isoDate }),
        ]);
      } else if (viewMode === "week") {
        const startIso = new Date(weekDates[0]).toISOString();
        const endIso = new Date(weekDates[6] + "T23:59:59Z").toISOString();
        [appts, slotList] = await Promise.all([
          AppointmentAPI.list({ start_date: startIso, end_date: endIso }),
          AppointmentAPI.listSlots({ start_date: startIso, end_date: endIso }),
        ]);
      } else {
        const [year, month] = selectedDate.split("-").map(Number);
        const startIso = new Date(`${year}-${String(month).padStart(2, "0")}-01`).toISOString();
        const endIso = new Date(`${year}-${String(month + 1 > 12 ? 1 : month + 1).padStart(2, "0")}-01`).toISOString();
        [appts, slotList] = await Promise.all([
          AppointmentAPI.list({ start_date: startIso, end_date: endIso }),
          AppointmentAPI.listSlots({ start_date: startIso, end_date: endIso }),
        ]);
      }
      setAppointments(appts);
      setSlots(slotList);
    } catch (e: any) {
      toast.error(e.message || "Failed to load appointments");
    } finally {
      setLoading(false);
    }
  }, [selectedDate, viewMode, weekDates]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Blocks aren't returned by AppointmentAPI.list/listSlots (a separate
  // resource), and listBlockTime is per-doctor with no date filter - fetched
  // once per doctor and bucketed by date client-side below, the same way
  // appointments already are. A supplementary overlay, not core calendar
  // data, so a failed fetch degrades to "no blocks shown" rather than
  // blocking the page.
  const fetchBlocks = useCallback(async () => {
    if (doctors.length === 0) { setBlocks([]); return; }
    const lists = await Promise.all(
      doctors.map((d) => DoctorAPI.listBlockTime(d.doctor_id).catch(() => [] as DoctorBlockTime[])),
    );
    setBlocks(lists.flat());
  }, [doctors]);

  useEffect(() => { fetchBlocks(); }, [fetchBlocks]);
  // Walk-in rail data - day view only. Queue is per-doctor, so fetch each
  // doctor's queue for the selected date in parallel.
  useEffect(() => {
    if (viewMode !== "day" || doctors.length === 0) {
      setWalkinsByDoctor({});
      return;
    }
    let cancelled = false;
    Promise.all(
      doctors.map((d) =>
        QueueAPI.list({ doctor_id: d.doctor_id, queue_date: selectedDate }).catch(() => [] as QueueEntryListItem[]),
      ),
    ).then((lists) => {
      if (cancelled) return;
      const byDoctor: Record<string, QueueEntryListItem[]> = {};
      doctors.forEach((d, i) => { byDoctor[d.doctor_id] = lists[i]; });
      setWalkinsByDoctor(byDoctor);
    });
    return () => { cancelled = true; };
  }, [doctors, selectedDate, viewMode]);

  // Lookups
  const doctorColorMap = useMemo(() => {
    const m: Record<string, number> = {};
    doctors.forEach((d, i) => { m[d.doctor_id] = i % DOCTOR_PALETTE.length; });
    return m;
  }, [doctors]);

  const doctorNameMap = useMemo(() => {
    const m: Record<string, string> = {};
    doctors.forEach((d) => { m[d.doctor_id] = d.name; });
    return m;
  }, [doctors]);

  const slotMap = useMemo(() => {
    const m: Record<string, AppointmentSlot> = {};
    slots.forEach((s) => { m[s.slot_id] = s; });
    return m;
  }, [slots]);

  const enriched: EnrichedAppt[] = useMemo(() => {
    return appointments
      .map((a) => {
        // Walk-ins (slot_id=null) have no slot to look up - they don't render
        // on this grid yet; the walk-in rail is added in a later phase.
        const slot = a.slot_id ? slotMap[a.slot_id] : undefined;
        if (!slot) return null;
        return {
          ...a,
          start_time: slot.start_time,
          end_time: slot.end_time,
          doctor_name: doctorNameMap[a.doctor_id],
          colorIdx: doctorColorMap[a.doctor_id] ?? 0,
        };
      })
      .filter(Boolean) as EnrichedAppt[];
  }, [appointments, slotMap, doctorNameMap, doctorColorMap]);

  const stats = useMemo(() => {
    const s = { total: appointments.length, scheduled: 0, confirmed: 0, in_progress: 0, completed: 0, cancelled: 0, no_show: 0 };
    appointments.forEach((a) => { (s as any)[a.status] = ((s as any)[a.status] ?? 0) + 1; });
    return s;
  }, [appointments]);

  const doctorStats = useMemo(() => {
    const m: Record<string, number> = {};
    appointments.forEach((a) => { m[a.doctor_id] = (m[a.doctor_id] ?? 0) + 1; });
    return Object.entries(m).map(([id, count]) => ({
      id, name: doctorNameMap[id] ?? id.slice(0, 8), count, colorIdx: doctorColorMap[id] ?? 0,
    }));
  }, [appointments, doctorNameMap, doctorColorMap]);

  const filteredEnriched = useMemo(
    () => filterDoctorId ? enriched.filter((a) => a.doctor_id === filterDoctorId) : enriched,
    [enriched, filterDoctorId]
  );

  // Group enriched appointments by date string for week/month views
  const apptsByDate = useMemo(() => {
    const m: Record<string, EnrichedAppt[]> = {};
    filteredEnriched.forEach((a) => {
      const d = toDateStr(new Date(a.start_time));
      if (!m[d]) m[d] = [];
      m[d].push(a);
    });
    return m;
  }, [filteredEnriched]);

  const apptCountByDate = useMemo(() => {
    const m: Record<string, number> = {};
    filteredEnriched.forEach((a) => {
      const d = toDateStr(new Date(a.start_time));
      m[d] = (m[d] ?? 0) + 1;
    });
    return m;
  }, [filteredEnriched]);

  const filteredBlocks = useMemo(
    () => (filterDoctorId ? blocks.filter((b) => b.doctor_id === filterDoctorId) : blocks),
    [blocks, filterDoctorId],
  );

  // Same rule the backend enforces (_require_can_manage_block): Manage Doctors
  // blocks any doctor's time, Manage Doctor Schedule only your own, nobody
  // else any - mirrored here so the drag affordance and the cancel button only
  // appear for people whose action would actually succeed.
  const canManageBlocksFor = useCallback((doctorId: string) => {
    if (canBlockAnyDoctor) return true;
    if (!canBlockOwnTime) return false;
    const doctor = doctors.find((d) => d.doctor_id === doctorId);
    return !!doctor && !!currentUserId && (doctor.user_id === currentUserId || doctor.doctor_id === currentUserId);
  }, [canBlockAnyDoctor, canBlockOwnTime, doctors, currentUserId]);
  const canDragBlock = !!filterDoctorId && canManageBlocksFor(filterDoctorId);

  // The dates the current view shows - blocks are bucketed only into these,
  // so a long vacation or a months-long series costs one entry per visible day.
  const visibleRange = useMemo((): [string, string] => {
    if (viewMode === "day") return [selectedDate, selectedDate];
    if (viewMode === "week") return [weekDates[0], weekDates[6]];
    const [year, month] = selectedDate.split("-").map(Number);
    return [`${year}-${String(month).padStart(2, "0")}-01`, toDateStr(new Date(year, month, 0))];
  }, [viewMode, selectedDate, weekDates]);

  // A block is listed under EVERY day it covers, not just its start date -
  // a week-long vacation shows on each of its days.
  const blocksByDate = useMemo(() => {
    const m: Record<string, DoctorBlockTime[]> = {};
    const [firstVisible, lastVisible] = visibleRange;
    filteredBlocks.forEach((b) => {
      const firstDay = toDateStr(new Date(b.start_time));
      const lastDay = toDateStr(blockLastInstant(b));
      const from = firstDay > firstVisible ? firstDay : firstVisible;
      const to = lastDay < lastVisible ? lastDay : lastVisible;
      for (const d = new Date(`${from}T00:00:00`); toDateStr(d) <= to; d.setDate(d.getDate() + 1)) {
        const key = toDateStr(d);
        if (!m[key]) m[key] = [];
        m[key].push(b);
      }
    });
    return m;
  }, [filteredBlocks, visibleRange]);

  // Unfiltered, every doctor's blocks share one column - say whose it is, or
  // one doctor's block reads as the whole clinic being closed.
  const blockOwnerLabel = (b: DoctorBlockTime, short = false) => {
    if (filterDoctorId) return "";
    const name = doctorNameMap[b.doctor_id];
    if (!name) return "";
    return ` · ${short ? name.split(" ").pop() : name}`;
  };

  const blockAccentStyle = (b: DoctorBlockTime): React.CSSProperties =>
    filterDoctorId ? {} : { borderLeft: `3px solid ${DOCTOR_PALETTE[doctorColorMap[b.doctor_id] ?? 0].hex}` };

  const confirmCancelBlock = async (scope: BlockCancelScope) => {
    if (!cancelTarget) return;
    setCancellingBlock(true);
    try {
      await DoctorAPI.cancelBlockTime(cancelTarget.doctor_id, cancelTarget.block_id, scope);
      toast.success(scope === "following" ? "This and all later occurrences cancelled" : "Block cancelled");
      setCancelTarget(null);
      fetchBlocks();
      fetchData();
    } catch (e: any) {
      toast.error(e.message || "Failed to cancel block");
    } finally {
      setCancellingBlock(false);
    }
  };

  useEffect(() => {
    if (!cancelTarget) return;
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === "Escape" && !cancellingBlock) setCancelTarget(null); };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [cancelTarget, cancellingBlock]);

  const openBlockModalForRange = (doctorId: string, dateStr: string, startH: number, endH: number) => {
    setBlockModalDoctorId(doctorId);
    setBlockModalRange({ start: hourToLocalDT(dateStr, startH), end: hourToLocalDT(dateStr, endH) });
  };

  // Drag-select only makes sense once a single doctor is filtered: the grid
  // overlays every doctor's appointments in one column with no per-doctor
  // split, so an unfiltered drag would leave "which doctor is this block
  // for" undefined. Requiring the filter first is the honest fix, not a
  // doctor-picker popup bolted onto the middle of a drag gesture. It's also
  // off for anyone who may not block that doctor's time (canDragBlock).
  const handleGridMouseDown = (dateStr: string) => (e: React.MouseEvent<HTMLDivElement>) => {
    if (!canDragBlock) return;
    if ((e.target as HTMLElement).closest("[data-interactive-block]")) return;
    const containerTop = e.currentTarget.getBoundingClientRect().top;
    const y = e.clientY - containerTop;
    setDrag({ dateStr, containerTop, startY: y, currentY: y });
  };

  useEffect(() => {
    if (!drag) return;
    const onMove = (e: MouseEvent) => {
      setDrag((d) => (d ? { ...d, currentY: e.clientY - d.containerTop } : d));
    };
    const onUp = () => {
      setDrag((d) => {
        if (d && filterDoctorId && canDragBlock) {
          const yTop = Math.min(d.startY, d.currentY);
          const yBottom = Math.max(d.startY, d.currentY);
          if (yBottom - yTop > 8) {
            const startH = roundToQuarterHour(yToHour(yTop));
            const endH = Math.max(roundToQuarterHour(yToHour(yBottom)), startH + 0.25);
            openBlockModalForRange(filterDoctorId, d.dateStr, startH, endH);
          }
        }
        return null;
      });
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [drag, filterDoctorId, canDragBlock]);

  const getBlockStyle = (appt: EnrichedAppt): React.CSSProperties => {
    const start = new Date(appt.start_time);
    const end = new Date(appt.end_time);
    const startH = start.getHours() + start.getMinutes() / 60;
    const endH = end.getHours() + end.getMinutes() / 60;
    const top = (startH - DAY_START) * HOUR_PX;
    const height = Math.max((endH - startH) * HOUR_PX, 44);
    const color = getApptColor(appt.status, appt.colorIdx);
    return {
      top: `${top}px`,
      height: `${height}px`,
      backgroundColor: color.light,
      borderLeft: `3px solid ${color.hex}`,
      boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    };
  };

  // One block's bar on one day's grid (day view: compact=false; week view:
  // compact=true). Blocks outside the grid's hours render as a labelled edge
  // marker so they're still visible and never mispositioned.
  const renderBlockBar = (block: DoctorBlockTime, dateStr: string, compact: boolean) => {
    const { style, edge } = getBlockSegment(block, dateStr);
    const reason = block.reason ? `: ${block.reason}` : "";
    const label = edge
      ? `Blocked ${fmtTime(block.start_time)}–${fmtTime(block.end_time)}${blockOwnerLabel(block, compact)}`
      : compact
        ? `Blocked${blockOwnerLabel(block, true)}`
        : `Blocked${blockOwnerLabel(block)}${reason}`;
    return (
      <div
        key={block.block_id}
        data-interactive-block
        data-testid="block-bar"
        title={`${fmtBlockRange(block)}${block.reason ? ` - ${block.reason}` : ""}`}
        className={`absolute ${compact ? "left-1 right-1 rounded px-1.5" : "left-2 right-3 rounded-lg px-2.5"} ${edge ? "py-0" : compact ? "py-1" : "py-1.5"} z-10 overflow-hidden border border-red-200`}
        style={{ ...style, ...blockAccentStyle(block), background: BLOCK_STRIPES }}
      >
        <div className="flex items-center justify-between gap-1">
          <span className={`${compact ? "text-[9px]" : "text-[11px]"} font-semibold text-red-700 truncate`}>{label}</span>
          {canManageBlocksFor(block.doctor_id) && (
            <button
              onClick={(e) => { e.stopPropagation(); setCancelTarget(block); }}
              className={`text-red-400 hover:text-red-600 ${compact ? "text-[10px]" : "text-xs px-1"} leading-none flex-shrink-0`}
              title="Cancel block"
              aria-label="Cancel block"
            >×</button>
          )}
        </div>
      </div>
    );
  };

  const railEntries = useMemo(() => {
    const all = Object.values(walkinsByDoctor).flat();
    return filterDoctorId ? all.filter((e) => e.doctor_id === filterDoctorId) : all;
  }, [walkinsByDoctor, filterDoctorId]);

  // Rail items are positioned by suggested_time on the same HOUR_PX/DAY_START
  // scale as getBlockStyle, so they visually align with concurrent scheduled
  // blocks - but in a separate column, never sharing horizontal space. That's
  // what lets this skip the overlap-resolution algorithm the main grid
  // doesn't have: two things can never collide if they never share a lane.
  const getRailItemStyle = (entry: QueueEntryListItem): React.CSSProperties => {
    const t = new Date(entry.suggested_time ?? entry.joined_at);
    const h = t.getHours() + t.getMinutes() / 60;
    const top = Math.max(0, (h - DAY_START) * HOUR_PX);
    const color = DOCTOR_PALETTE[doctorColorMap[entry.doctor_id] ?? 0];
    return {
      top: `${top}px`,
      height: "40px",
      backgroundColor: color.light,
      borderLeft: `3px dashed ${color.hex}`,
      boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    };
  };

  const navigate = (direction: number) => {
    const d = new Date(selectedDate + "T00:00:00");
    if (viewMode === "day") d.setDate(d.getDate() + direction);
    else if (viewMode === "week") d.setDate(d.getDate() + 7 * direction);
    else d.setMonth(d.getMonth() + direction);
    const newDate = toDateStr(d);
    if (newDate < minDate || newDate > maxDate) return;
    setSelectedDate(newDate);
  };

  const isToday = (dateStr: string) => dateStr === today;

  const headerLabel = () => {
    if (viewMode === "day") {
      return isToday(selectedDate) ? "Today" : fmtLongDate(selectedDate).split(",")[0];
    }
    if (viewMode === "week") {
      const [y, m, d] = weekDates[0].split("-").map(Number);
      const [, , d2] = weekDates[6].split("-").map(Number);
      const [, m2, ] = weekDates[6].split("-").map(Number);
      if (m === m2) return `${MONTH_LABELS[m - 1]} ${d}–${d2}, ${y}`;
      return `${MONTH_LABELS[m - 1]} ${d} – ${MONTH_LABELS[m2 - 1]} ${d2}`;
    }
    const [y, m] = selectedDate.split("-").map(Number);
    return `${MONTH_LABELS[m - 1]} ${y}`;
  };

  // ── Day view ──────────────────────────────────────────────────────────────
  const renderDayView = () => (
    <div className="flex-1 overflow-y-auto relative">
      <div className="flex min-h-full">
        <div className="w-14 flex-shrink-0 bg-white border-r border-slate-100 sticky left-0 z-10">
          {HOURS.map((h) => (
            <div key={h} className="border-b border-slate-100 flex items-start justify-end pr-2 pt-1" style={{ height: HOUR_PX }}>
              <span className="text-[11px] text-slate-400 -translate-y-2">
                {h === 12 ? "12 PM" : h > 12 ? `${h - 12} PM` : `${h} AM`}
              </span>
            </div>
          ))}
        </div>
        <div
          data-testid="day-grid"
          className={`flex-1 relative ${canDragBlock ? "cursor-crosshair" : ""}`}
          style={{ height: HOURS.length * HOUR_PX }}
          onMouseDown={handleGridMouseDown(selectedDate)}
        >
          {HOURS.map((h) => (
            <div key={h} className="absolute left-0 right-0 border-b border-slate-100" style={{ top: (h - DAY_START) * HOUR_PX, height: HOUR_PX }} />
          ))}
          {HOURS.map((h) => (
            <div key={`half-${h}`} className="absolute left-0 right-0 border-b border-dashed border-slate-50" style={{ top: (h - DAY_START) * HOUR_PX + HOUR_PX / 2 }} />
          ))}
          {drag && drag.dateStr === selectedDate && (
            <div
              className="absolute left-2 right-3 rounded-lg bg-cyan-200/50 border-2 border-dashed border-cyan-400 z-20 pointer-events-none"
              style={{
                top: `${Math.min(drag.startY, drag.currentY)}px`,
                height: `${Math.max(Math.abs(drag.currentY - drag.startY), 2)}px`,
              }}
            />
          )}
          {(blocksByDate[selectedDate] ?? []).map((block) => renderBlockBar(block, selectedDate, false))}
          {isToday(selectedDate) && (() => {
            const now = new Date();
            const nowH = now.getHours() + now.getMinutes() / 60;
            if (nowH < DAY_START || nowH > DAY_END) return null;
            return (
              <div className="absolute left-0 right-0 z-20 flex items-center pointer-events-none" style={{ top: (nowH - DAY_START) * HOUR_PX }}>
                <div className="w-2.5 h-2.5 rounded-full bg-cyan-500 -ml-1.5 flex-shrink-0" />
                <div className="flex-1 h-[2px] bg-cyan-400 opacity-80" />
              </div>
            );
          })()}
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/60">
              <div className="text-sm text-slate-400 animate-pulse">Loading…</div>
            </div>
          )}
          {!loading && filteredEnriched.length === 0 && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 gap-2">
              <svg className="w-10 h-10 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-sm">{filterDoctorId ? "No appointments for this doctor" : "No appointments scheduled"}</p>
              {!filterDoctorId && canBookAppointment && <button onClick={() => setIsBookOpen(true)} className="text-xs text-cyan-600 hover:underline">+ Book one now</button>}
            </div>
          )}
          {filteredEnriched.map((appt) => {
            const color = getApptColor(appt.status, appt.colorIdx);
            return (
              <div
                key={appt.appointment_id}
                data-interactive-block
                role="button"
                tabIndex={0}
                className="absolute left-2 right-3 rounded-lg px-2.5 py-1.5 cursor-pointer hover:brightness-95 transition-all z-10 overflow-hidden"
                style={getBlockStyle(appt)}
                onMouseEnter={() => setHoveredAppt(appt)}
                onMouseLeave={() => setHoveredAppt(null)}
                onClick={() => openAppointment(appt.appointment_id)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openAppointment(appt.appointment_id); } }}
              >
                <div className="flex items-center justify-between gap-1 mb-0.5">
                  <div className="text-[11px] font-semibold truncate" style={{ color: color.hex }}>{appt.doctor_name ?? "Doctor"}</div>
                  <StatusDot status={appt.status} />
                </div>
                <div className="text-[11px] text-slate-600 truncate">{fmtTime(appt.start_time)} – {fmtTime(appt.end_time)}</div>
              </div>
            );
          })}
        </div>

        {/* Walk-in rail: separate lane, positioned by live ETA rather than a
            reserved slot - never shares horizontal space with the scheduled
            grid, so no lane-collision logic is needed. */}
        <div className="w-32 flex-shrink-0 border-l border-slate-100 relative bg-purple-50/20" style={{ height: HOURS.length * HOUR_PX }}>
          {HOURS.map((h) => (
            <div key={h} className="absolute left-0 right-0 border-b border-slate-100" style={{ top: (h - DAY_START) * HOUR_PX, height: HOUR_PX }} />
          ))}
          {railEntries.map((entry) => (
            <div
              key={entry.queue_entry_id}
              role="button"
              tabIndex={0}
              title={`${entry.patient_name} - ${entry.doctor_name} - ETA ${entry.eta_minutes ?? "?"}m`}
              className="absolute left-1 right-1 rounded-md px-1.5 py-1 cursor-pointer hover:brightness-95 transition-all z-10 overflow-hidden border border-dashed border-purple-200"
              style={getRailItemStyle(entry)}
              onClick={() => entry.appointment_id && openAppointment(entry.appointment_id)}
              onKeyDown={(e) => {
                if ((e.key === "Enter" || e.key === " ") && entry.appointment_id) {
                  e.preventDefault();
                  openAppointment(entry.appointment_id);
                }
              }}
            >
              <div className="text-[10px] font-semibold text-slate-700 truncate">{entry.patient_name}</div>
              <div className="flex items-center gap-1">
                <QueueStateBadge state={entry.state} size="sm" />
                <span className="text-[9px] text-slate-500 truncate">
                  {entry.eta_minutes != null ? `~${entry.eta_minutes}m` : "—"}
                </span>
              </div>
            </div>
          ))}
          {railEntries.length === 0 && (
            <div className="absolute inset-x-2 top-2 text-[10px] text-slate-300 text-center">No walk-ins</div>
          )}
        </div>
      </div>
    </div>
  );

  // ── Week view ─────────────────────────────────────────────────────────────
  const renderWeekView = () => (
    <div className="flex-1 overflow-auto">
      <div className="flex min-w-[640px]">
        {/* Time labels */}
        <div className="w-14 flex-shrink-0 bg-white border-r border-slate-100 sticky left-0 z-20">
          <div className="h-10 border-b border-slate-100" />
          {HOURS.map((h) => (
            <div key={h} className="border-b border-slate-100 flex items-start justify-end pr-2 pt-1" style={{ height: HOUR_PX }}>
              <span className="text-[11px] text-slate-400 -translate-y-2">
                {h === 12 ? "12 PM" : h > 12 ? `${h - 12} PM` : `${h} AM`}
              </span>
            </div>
          ))}
        </div>

        {/* Day columns */}
        <div className="flex flex-1">
          {weekDates.map((dateStr, colIdx) => {
            const date = new Date(dateStr + "T00:00:00");
            const dayLabel = DAY_LABELS[date.getDay()];
            const dayNum = date.getDate();
            const isSelected = dateStr === selectedDate;
            const isTodayCol = isToday(dateStr);
            const dayAppts = apptsByDate[dateStr] ?? [];
            const dayBlocks = blocksByDate[dateStr] ?? [];

            return (
              <div key={dateStr} className={`flex-1 flex flex-col border-r border-slate-100 ${colIdx === 6 ? "border-r-0" : ""}`}>
                {/* Column header */}
                <div
                  className={`h-10 flex flex-col items-center justify-center border-b border-slate-100 cursor-pointer ${isSelected ? "bg-cyan-50" : "bg-white hover:bg-slate-50"}`}
                  onClick={() => { setSelectedDate(dateStr); setViewMode("day"); }}
                >
                  <span className="text-[10px] text-slate-400 font-medium">{dayLabel}</span>
                  <span className={`text-sm font-bold ${isTodayCol ? "text-cyan-600" : "text-slate-700"}`}>{dayNum}</span>
                </div>

                {/* Time grid */}
                <div
                  className={`relative flex-1 ${canDragBlock ? "cursor-crosshair" : ""}`}
                  style={{ height: HOURS.length * HOUR_PX }}
                  onMouseDown={handleGridMouseDown(dateStr)}
                >
                  {HOURS.map((h) => (
                    <div key={h} className="absolute left-0 right-0 border-b border-slate-100" style={{ top: (h - DAY_START) * HOUR_PX, height: HOUR_PX }} />
                  ))}

                  {/* Current time line */}
                  {isTodayCol && (() => {
                    const now = new Date();
                    const nowH = now.getHours() + now.getMinutes() / 60;
                    if (nowH < DAY_START || nowH > DAY_END) return null;
                    return (
                      <div className="absolute left-0 right-0 z-20 flex items-center pointer-events-none" style={{ top: (nowH - DAY_START) * HOUR_PX }}>
                        <div className="flex-1 h-[2px] bg-cyan-400 opacity-80" />
                      </div>
                    );
                  })()}

                  {drag && drag.dateStr === dateStr && (
                    <div
                      className="absolute left-1 right-1 rounded bg-cyan-200/50 border-2 border-dashed border-cyan-400 z-20 pointer-events-none"
                      style={{
                        top: `${Math.min(drag.startY, drag.currentY)}px`,
                        height: `${Math.max(Math.abs(drag.currentY - drag.startY), 2)}px`,
                      }}
                    />
                  )}

                  {dayBlocks.map((block) => renderBlockBar(block, dateStr, true))}

                  {dayAppts.map((appt) => {
                    const color = getApptColor(appt.status, appt.colorIdx);
                    return (
                      <div
                        key={appt.appointment_id}
                        data-interactive-block
                        role="button"
                        tabIndex={0}
                        className="absolute left-1 right-1 rounded px-1.5 py-1 cursor-pointer hover:brightness-95 transition-all z-10 overflow-hidden"
                        style={getBlockStyle(appt)}
                        onMouseEnter={() => setHoveredAppt(appt)}
                        onMouseLeave={() => setHoveredAppt(null)}
                        onClick={() => openAppointment(appt.appointment_id)}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openAppointment(appt.appointment_id); } }}
                      >
                        <div className="text-[10px] font-semibold truncate" style={{ color: color.hex }}>{appt.doctor_name ?? "Dr."}</div>
                        <div className="text-[9px] text-slate-500 truncate">{fmtTime(appt.start_time)}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  // ── Month view ────────────────────────────────────────────────────────────
  const renderMonthView = () => {
    const [year, month] = selectedDate.split("-").map(Number);
    const firstDay = new Date(year, month - 1, 1);
    const daysInMonth = new Date(year, month, 0).getDate();
    const startDow = firstDay.getDay(); // 0=Sun

    const cells: (string | null)[] = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push(`${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
    }
    while (cells.length % 7 !== 0) cells.push(null);

    const rows: (string | null)[][] = [];
    for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));

    return (
      <div className="flex-1 overflow-auto p-4">
        <div className="grid grid-cols-7 gap-px bg-slate-100 rounded-xl overflow-hidden border border-slate-100">
          {/* Day headers */}
          {DAY_LABELS.map((d) => (
            <div key={d} className="bg-white text-center py-2 text-[11px] font-semibold text-slate-400">{d}</div>
          ))}

          {/* Day cells */}
          {cells.map((dateStr, idx) => {
            if (!dateStr) {
              return <div key={`empty-${idx}`} className="bg-white min-h-[80px]" />;
            }
            const count = apptCountByDate[dateStr] ?? 0;
            const isTodayCell = isToday(dateStr);
            const isSelected = dateStr === selectedDate;
            const dayNum = parseInt(dateStr.split("-")[2], 10);
            const dayAppts = (apptsByDate[dateStr] ?? []).slice(0, 3);
            const dayBlocks = blocksByDate[dateStr] ?? [];

            return (
              <div
                key={dateStr}
                className={`bg-white min-h-[80px] p-2 cursor-pointer transition-colors hover:bg-slate-50 ${isSelected ? "ring-2 ring-inset ring-cyan-400" : ""}`}
                onClick={() => { setSelectedDate(dateStr); setViewMode("day"); }}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-sm font-bold w-6 h-6 flex items-center justify-center rounded-full ${isTodayCell ? "bg-cyan-500 text-white" : "text-slate-700"}`}>
                    {dayNum}
                  </span>
                  {count > 0 && (
                    <span className="text-[10px] bg-slate-100 text-slate-500 rounded-full px-1.5 py-0.5 font-medium">{count}</span>
                  )}
                </div>
                <div className="space-y-0.5">
                  {dayBlocks.length > 0 && (
                    <div
                      data-testid="month-block"
                      className="text-[10px] truncate rounded px-1 py-0.5 font-semibold text-red-700 border border-red-200"
                      style={{ background: BLOCK_STRIPES }}
                      title={dayBlocks.map((b) => `${doctorNameMap[b.doctor_id] ?? "Doctor"}: ${fmtBlockRange(b)}`).join("\n")}
                    >
                      {dayBlocks.length === 1 ? `Blocked${blockOwnerLabel(dayBlocks[0], true)}` : `${dayBlocks.length} blocks`}
                    </div>
                  )}
                  {dayAppts.map((a) => {
                    const aColor = getApptColor(a.status, a.colorIdx);
                    return (
                      <div
                        key={a.appointment_id}
                        role="button"
                        tabIndex={0}
                        className="text-[10px] truncate rounded px-1 py-0.5 font-medium cursor-pointer hover:brightness-95 transition-all"
                        style={{ backgroundColor: aColor.light, color: aColor.hex }}
                        onClick={(e) => { e.stopPropagation(); openAppointment(a.appointment_id); }}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); openAppointment(a.appointment_id); } }}
                      >
                        {fmtTime(a.start_time)} {a.doctor_name?.split(" ").pop() ?? "Dr."}
                      </div>
                    );
                  })}
                  {count > 3 && (
                    <div className="text-[10px] text-slate-400">+{count - 3} more</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ── Right panel ───────────────────────────────────────────────────────────
  const renderRightPanel = () => (
    <div className="w-72 max-w-full h-full flex-shrink-0 bg-white border-l border-slate-100 overflow-y-auto shadow-xl lg:shadow-none">
      <div className="p-5 space-y-6">
        <div>
          <h3 className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-3">
            {viewMode === "day" ? (isToday(selectedDate) ? "Today's" : "Day's") : viewMode === "week" ? "Week's" : "Month's"} Stats
          </h3>
          <div className="grid grid-cols-2 gap-2">
            {[
              { label: "Total", value: stats.total, cls: "text-slate-800" },
              { label: "Confirmed", value: stats.confirmed, cls: "text-cyan-600" },
              { label: "In Progress", value: stats.in_progress, cls: "text-amber-600" },
              { label: "Completed", value: stats.completed, cls: "text-green-600" },
              { label: "Scheduled", value: stats.scheduled, cls: "text-blue-600" },
              { label: "No Show", value: stats.no_show, cls: "text-red-500" },
            ].map(({ label, value, cls }) => (
              <div key={label} className="bg-slate-50 rounded-xl px-3 py-2.5">
                <div className={`text-xl font-bold ${cls}`}>{value}</div>
                <div className="text-[11px] text-slate-400 mt-0.5">{label}</div>
              </div>
            ))}
          </div>
        </div>

        {doctorStats.length > 0 && (
          <div>
            <h3 className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-3">Active Doctors</h3>
            <div className="space-y-2">
              {doctorStats.map(({ id, name, count, colorIdx }) => (
                <div key={id} className="flex items-center gap-2.5">
                  <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: DOCTOR_PALETTE[colorIdx].hex }} />
                  <span className="text-sm text-slate-700 flex-1 truncate">{name}</span>
                  <span className="text-xs font-medium text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">{count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {doctors.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Doctors</h3>
              {filterDoctorId && (
                <button onClick={() => setFilterDoctorId(null)} className="text-[10px] text-cyan-600 hover:underline">
                  Clear filter
                </button>
              )}
            </div>
            <div className="space-y-1">
              {doctors.slice(0, 8).map((d) => {
                const colorIdx = doctorColorMap[d.doctor_id] ?? 0;
                const isActive = filterDoctorId === d.doctor_id;
                return (
                  <button
                    key={d.doctor_id}
                    onClick={() => setFilterDoctorId(isActive ? null : d.doctor_id)}
                    className={`w-full flex items-center gap-2.5 px-2 py-1.5 rounded-lg text-left transition ${
                      isActive ? "bg-cyan-50 ring-1 ring-cyan-200" : "hover:bg-slate-50"
                    }`}
                  >
                    <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: DOCTOR_PALETTE[colorIdx].hex }} />
                    <span className={`text-xs flex-1 truncate ${isActive ? "text-cyan-700 font-medium" : "text-slate-600"}`}>{d.name}</span>
                    <span className="text-[10px] text-slate-400">{d.specialization}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {hoveredAppt && (
          <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100 space-y-2">
            <h3 className="text-xs font-semibold text-slate-700 mb-2">Appointment</h3>
            <div className="flex justify-between text-xs">
              <span className="text-slate-400">Doctor</span>
              <span className="font-medium text-slate-700">{hoveredAppt.doctor_name ?? "—"}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-slate-400">Time</span>
              <span className="font-medium text-slate-700">{fmtTime(hoveredAppt.start_time)} – {fmtTime(hoveredAppt.end_time)}</span>
            </div>
            <div className="flex justify-between text-xs items-center">
              <span className="text-slate-400">Status</span>
              <AppointmentStatusBadge status={hoveredAppt.status} size="sm" />
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-[#f7f8fc] overflow-hidden">
      <Sidebar />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden lg:pl-[var(--sidebar-w)] transition-[padding-left] duration-300 ease-in-out pt-14 lg:pt-0">
        <AppHeader title="Appointment Calendar" searchMode="both" />
        {/* ── Top bar ───────────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-slate-100 flex-shrink-0 gap-3 flex-wrap">
          {/* Left: back + date-step nav + title */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => routerNavigate("/appointments")}
              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-800 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Back to appointments"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={() => navigate(-1)}
              disabled={selectedDate <= minDate}
              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition text-lg leading-none disabled:opacity-30"
            >‹</button>
            <div>
              <h1 className="text-sm font-semibold text-slate-800 leading-tight">{headerLabel()}</h1>
              {viewMode === "day" && <p className="text-xs text-slate-400">{fmtLongDate(selectedDate)}</p>}
            </div>
            <button
              onClick={() => navigate(1)}
              disabled={selectedDate >= maxDate}
              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition text-lg leading-none disabled:opacity-30"
            >›</button>
            {selectedDate !== today && (
              <button
                onClick={() => setSelectedDate(today)}
                className="text-xs px-2.5 py-1 rounded-lg bg-slate-100 text-cyan-600 hover:bg-cyan-50 transition"
              >Today</button>
            )}
          </div>

          {/* Center: View toggle */}
          <div className="flex items-center gap-2">
            <div className="flex items-center bg-slate-100 rounded-xl p-1 gap-0.5">
              {(["day", "week", "month"] as ViewMode[]).map((v) => (
                <button
                  key={v}
                  onClick={() => setViewMode(v)}
                  className={`px-3 py-1 rounded-lg text-xs font-medium transition capitalize ${viewMode === v ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                >{v}</button>
              ))}
            </div>
            {viewMode !== "month" && !filterDoctorId && (canBlockAnyDoctor || canBlockOwnTime) && (
              <span className="hidden md:inline text-[11px] text-slate-400">
                {canBlockAnyDoctor ? "Select a doctor on the right to drag-block their time" : "Select yourself on the right to drag-block your time"}
              </span>
            )}
          </div>

          {/* Right: date picker + actions */}
          <div className="flex items-center gap-2">
            {viewMode === "day" && (
              <DateInput
                value={selectedDate}
                min={minDate}
                max={maxDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 text-slate-600 outline-none focus:ring-2 focus:ring-cyan-300 bg-white"
              />
            )}
            {/* Mobile: stats toggle */}
            <button
              onClick={() => setShowRightPanel(!showRightPanel)}
              className="lg:hidden p-2 rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50"
              title="Toggle stats"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </button>
            {canBookAppointment && (
              <button
                onClick={() => setIsBookOpen(true)}
                className="flex items-center gap-1.5 bg-cyan-500 text-white px-3 py-2 rounded-xl text-sm font-medium hover:bg-cyan-600 transition shadow-sm flex-shrink-0"
              >
                <span className="text-base leading-none font-bold">+</span>
                <span className="hidden sm:inline">New Appointment</span>
              </button>
            )}
          </div>
        </div>

        {/* ── Body ─────────────────────────────────────────────────── */}
        <div className="flex flex-1 overflow-hidden">
          <div className="flex-1 flex flex-col overflow-hidden">
            {viewMode === "day" && renderDayView()}
            {viewMode === "week" && renderWeekView()}
            {viewMode === "month" && renderMonthView()}
          </div>

          {/* Right panel: always visible (in-flow) on lg, toggleable overlay drawer on
              mobile — fixed positioning so it covers the calendar instead of squeezing
              it down to an unusably narrow column. */}
          <div
            className={`${showRightPanel ? "fixed inset-y-0 right-0 z-40 block" : "hidden"} lg:static lg:z-auto lg:block lg:flex-shrink-0`}
          >
            {renderRightPanel()}
          </div>
        </div>
      </div>

      {/* Mobile stats overlay backdrop */}
      {showRightPanel && (
        <div className="lg:hidden fixed inset-0 z-30 bg-black/20" onClick={() => setShowRightPanel(false)} />
      )}

      <BookAppointmentModal
        isOpen={isBookOpen}
        onClose={() => setIsBookOpen(false)}
        onSuccess={fetchData}
        prefillDate={selectedDate}
      />

      <BlockDoctorTimeModal
        isOpen={blockModalDoctorId !== null}
        doctorId={blockModalDoctorId ?? ""}
        doctorName={blockModalDoctorId ? (doctorNameMap[blockModalDoctorId] ?? "") : ""}
        onClose={() => { setBlockModalDoctorId(null); setBlockModalRange(null); }}
        onSuccess={() => { fetchBlocks(); fetchData(); }}
        initialStart={blockModalRange?.start}
        initialEnd={blockModalRange?.end}
      />

      {cancelTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={() => { if (!cancellingBlock) setCancelTarget(null); }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="cancel-block-title"
            className="w-full max-w-sm bg-white rounded-2xl shadow-2xl p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="cancel-block-title" className="text-base font-semibold text-slate-800">Cancel blocked time?</h2>
            <p className="text-sm text-slate-600">
              {doctorNameMap[cancelTarget.doctor_id] ?? "Doctor"} · {fmtBlockRange(cancelTarget)}
              {cancelTarget.reason ? ` ("${cancelTarget.reason}")` : ""}. The slots it blocked will reopen.
            </p>
            {isSeriesBlock(cancelTarget) ? (
              <div className="space-y-2">
                <p className="text-xs text-slate-500">This block repeats.</p>
                <button
                  onClick={() => confirmCancelBlock("occurrence")}
                  disabled={cancellingBlock}
                  className="w-full border border-red-200 text-red-600 py-2.5 rounded-xl text-sm font-medium hover:bg-red-50 disabled:opacity-40 transition"
                >
                  Only this occurrence
                </button>
                <button
                  onClick={() => confirmCancelBlock("following")}
                  disabled={cancellingBlock}
                  className="w-full bg-red-500 text-white py-2.5 rounded-xl text-sm font-medium hover:bg-red-600 disabled:opacity-40 transition"
                >
                  This and all later occurrences
                </button>
              </div>
            ) : (
              <button
                onClick={() => confirmCancelBlock("occurrence")}
                disabled={cancellingBlock}
                className="w-full bg-red-500 text-white py-2.5 rounded-xl text-sm font-medium hover:bg-red-600 disabled:opacity-40 transition"
              >
                Cancel block
              </button>
            )}
            <button
              onClick={() => setCancelTarget(null)}
              disabled={cancellingBlock}
              className="w-full border border-slate-200 text-slate-600 py-2.5 rounded-xl text-sm hover:bg-slate-50 disabled:opacity-40 transition"
            >
              Keep it
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentCalendarPage;
